import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Breadcrumbs,
  Link,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid as MuiGrid,
  Chip,
  TablePagination,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterAlt as FilterAltIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import agentService, { Agent, Reservation, ReservationQueryParams } from '../../services/agentService';
import { format, parseISO } from 'date-fns';

// Use Grid as a custom component to fix MUI version issues
const Grid = MuiGrid;

// Reservation status options
const RESERVATION_STATUS_OPTIONS = [
  'All',
  'Booked',
  'Checked In',
  'Checked Out',
  'Cancelled',
  'No Show'
];

// Helper to format currency
const formatCurrency = (amount: number | null): string => {
  if (amount === null) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0
  }).format(amount);
};

// Helper to format dates
const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  try {
    return format(parseISO(dateString), 'dd MMM yyyy');
  } catch (error) {
    return dateString;
  }
};

const AgentReservations: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  
  // Pagination
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  
  // Format dates for API
  const formatDateForApi = (date: Date | null): string | undefined => {
    if (!date) return undefined;
    return format(date, 'yyyy-MM-dd');
  };
  
  // Fetch agent details
  const fetchAgent = useCallback(async () => {
    if (!id) return;
    
    try {
      const agentData = await agentService.getAgent(parseInt(id));
      setAgent(agentData);
    } catch (err) {
      console.error(`Failed to fetch agent with ID ${id}:`, err);
      setError('Failed to load agent details.');
    }
  }, [id]);
  
  // Fetch reservations
  const fetchReservations = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      
      // Prepare query parameters
      const queryParams: ReservationQueryParams = {
        page: page + 1,
        page_size: rowsPerPage,
        search: searchTerm || undefined,
        status: statusFilter !== 'All' ? statusFilter : undefined,
        start_date: formatDateForApi(startDate),
        end_date: formatDateForApi(endDate)
      };
      
      const response = await agentService.getAgentReservations(parseInt(id), queryParams);
      setReservations(response.results);
      setTotalCount(response.count);
    } catch (err) {
      console.error(`Failed to fetch reservations for agent ID ${id}:`, err);
      setError('Failed to load reservations.');
    } finally {
      setLoading(false);
    }
  }, [id, page, rowsPerPage, searchTerm, statusFilter, startDate, endDate]);
  
  // Initial data loading
  useEffect(() => {
    fetchAgent();
  }, [fetchAgent]);
  
  // Fetch reservations when filters change or pagination changes
  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);
  
  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };
  
  // Clear all filters
  const handleClearFilters = () => {
    setStatusFilter('All');
    setStartDate(null);
    setEndDate(null);
  };
  
  // Navigate to reservation details
  const handleViewReservation = (reservation: Reservation) => {
    navigate(`/reservations/${reservation.id}`);
  };
  
  // Go back to agent list
  const handleGoBack = () => {
    navigate('/agents');
  };
  
  // Get badge color based on reservation status
  const getStatusColor = (status: string): 'primary' | 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'Booked':
        return 'primary';
      case 'Checked In':
        return 'success';
      case 'Checked Out':
        return 'info';
      case 'Cancelled':
        return 'error';
      case 'No Show':
        return 'warning';
      default:
        return 'default';
    }
  };
  
  // Loading state
  if (!agent && loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Error state
  if (error && !agent) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={handleGoBack} sx={{ mt: 2 }}>
          Back to Agents
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Header with breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            onClick={handleGoBack}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            Agents
          </Link>
          <Typography color="text.primary">{agent?.name}</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Box>
            <Typography variant="h5" component="h1">
              {agent?.name} - Reservations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {agent?.reservation_count} total reservations
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
          >
            Back to Agents
          </Button>
        </Box>
      </Box>
      
      {/* Filter and search section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid style={{ gridColumn: 'span 8' }}>
            <TextField
              label="Search Reservations"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              variant="outlined"
              placeholder="Guest name, reservation #"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          </Grid>
          
          <Grid style={{ gridColumn: 'span 2' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as string)}
                label="Status"
              >
                {RESERVATION_STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid style={{ gridColumn: 'span 5' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="From Date"
                  value={startDate}
                  onChange={(date: Date | null) => setStartDate(date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
                <DatePicker
                  label="To Date"
                  value={endDate}
                  onChange={(date: Date | null) => setEndDate(date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Box>
            </LocalizationProvider>
          </Grid>
          
          <Grid style={{ gridColumn: 'span 2' }} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }} >
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleClearFilters}
              disabled={statusFilter === 'All' && !startDate && !endDate}
              size="small"
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Reservations Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Reservation #</TableCell>
                <TableCell>Guest</TableCell>
                <TableCell>Check-in</TableCell>
                <TableCell>Check-out</TableCell>
                <TableCell>Property</TableCell>
                <TableCell>Room Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && reservations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : reservations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    No reservations found.
                  </TableCell>
                </TableRow>
              ) : (
                reservations.map((reservation) => (
                  <TableRow 
                    key={reservation.id}
                    hover
                    onClick={() => handleViewReservation(reservation)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      {reservation.res_no || reservation.crs_no || `ID: ${reservation.id}`}
                    </TableCell>
                    <TableCell>{reservation.guest_name}</TableCell>
                    <TableCell>{formatDate(reservation.arrival_date)}</TableCell>
                    <TableCell>{formatDate(reservation.departure_date)}</TableCell>
                    <TableCell>{reservation.property_name}</TableCell>
                    <TableCell>{reservation.room_type || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={reservation.reservation_status}
                        size="small"
                        color={getStatusColor(reservation.reservation_status)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(reservation.res_value)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default AgentReservations; 